import styled from 'styled-components';

import { ITaskTone, SURFACE } from './taskTones';

/**
 * Быстрые действия и чипы связей — общая вёрстка карточки доски и строки
 * списка (ТЗ 0065).
 *
 * Отдельный файл, а не по копии в `TaskBoard.styled` и `TaskList.styled`:
 * ровно на таком дублировании уже разъехались цвета статуса и приоритета —
 * два файла, две правды, и никакого сигнала о расхождении (см. `taskTones`).
 *
 * 🔴 `position: relative; z-index: 1` на КАЖДОМ интерактивном элементе здесь —
 * не украшение. И карточка, и строка списка используют приём stretched link:
 * у кнопки названия есть `::after { inset: 0 }`, невидимой накладкой
 * закрывающий весь корпус, чтобы клик по пустому месту открывал задачу. Кнопка
 * без своего слоя оказывается ПОД этой накладкой: выглядит рабочей, наводится,
 * а клик уходит в «открыть задачу». Ровно поэтому `z-index: 1` уже стоит у
 * чекбокса и у ручки перетаскивания.
 */

/**
 * Полоса быстрых действий. Проявляется при наведении и при клавиатурном
 * фокусе — второе обязательно: действие, доступное только мыши, для
 * клавиатуры не существует.
 *
 * Место под неё занято ВСЕГДА (`visibility`, а не `display`), иначе карточка
 * подрастала под курсором и толкала соседние вниз.
 */
export const Actions = styled.div<{ $pinned?: boolean; $inline?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  /* В карточке полоса идёт СВОЕЙ строкой под содержимым, в строке списка —
     внутри правой мета-колонки, и отступ сверху там лишний. */
  margin-top: ${({ $inline }) => ($inline ? 0 : '6px')};
  /* $pinned — открыт поповер даты. Он живёт в портале у body, фокус уходит
     из карточки, и :focus-within гаснет вместе с полосой, из-под которой
     поповер вырос. Обратные кавычки внутри styled-шаблона обрывают его —
     писать их здесь нельзя. */
  visibility: ${({ $pinned }) => ($pinned ? 'visible' : 'hidden')};
  opacity: ${({ $pinned }) => ($pinned ? 1 : 0)};
  transition: opacity 0.12s ease;
`;

export const ActionButton = styled.button<{ $active?: boolean }>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  border: 1px solid
    ${({ $active }) => ($active ? SURFACE.accent : SURFACE.line)};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? SURFACE.accentSoft : SURFACE.card)};
  color: ${({ $active }) => ($active ? SURFACE.accent : '#475569')};
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: ${SURFACE.accentLine};
    background: ${SURFACE.accentSoft};
    color: ${SURFACE.accent};
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }

  svg {
    display: block;
  }
`;

/** Панель поповера «когда». */
export const DuePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 190px;
`;

export const DueRow = styled.div`
  display: flex;
  gap: 4px;
`;

/** Ряд чипов связей. Свой ряд, а не продолжение мета-строки: срок и приоритет
 *  говорят «когда и насколько срочно», связи — «о чём», и смешивать их в одну
 *  ленту значит потерять оба ответа. */
export const LinkRow = styled.div<{ $inline?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: ${({ $inline }) => ($inline ? 0 : '4px')};
  min-width: 0;
  /* Ряд не выпускает чипы за края. Замер на стенде: карточка 286px, ряд 264,
     а два чипа по своему потолку в 150 дают 304 — на текущих данных длиннее
     112px имён не было, то есть до перелива оставалось несколько символов.
     Название курса или оператор дофинансирования его бы устроили. */
  overflow: hidden;
`;

/** Чип связи. НЕ ссылка и не кнопка: любой интерактив здесь пришлось бы
 *  поднимать над накладкой названия, а вложенная в строку ссылка ещё и ломает
 *  перетаскивание. Открыть связанный объект можно из окна задачи. */
export const LinkChip = styled.span<{ $tone?: ITaskTone }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  /* Сжимается, а не только обрезается по потолку: при двух чипах в узкой
     колонке место делится между ними. */
  flex: 0 1 auto;
  min-width: 0;
  max-width: 150px;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  /* Цвет по виду связи (ТЗ 0109) — из linkKindTone. */
  background: ${({ $tone }) => $tone?.bg ?? SURFACE.column};
  border: 1px solid ${({ $tone }) => $tone?.line ?? SURFACE.line};
  color: ${({ $tone }) => $tone?.fg ?? '#5b6775'};
  font-size: 11px;
  overflow: hidden;
  white-space: nowrap;

  svg {
    display: block;
    flex-shrink: 0;
    color: ${({ $tone }) => $tone?.fg ?? SURFACE.mutedSoft};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const LinkMore = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${SURFACE.mutedSoft};
  white-space: nowrap;
`;

/** Счётчик реплик. Показывается только когда обсуждение есть: «0» сообщает,
 *  что говорить не о чем, — это шум в каждой карточке. */
export const CommentCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: ${SURFACE.mutedSoft};

  svg {
    display: block;
  }
`;
